import { createAction, props } from '@ngrx/store';
import { Team, TeamsPagination, TeamWorkspaceManagementSettings } from '../../models/team.model';
import { TeamMember } from '@/models/team-member.model';

// Load Teams
export const loadTeams = createAction('[Teams] Load Teams', props<{ page: number; limit: number; name?: string }>());

export const loadTeamsSuccess = createAction(
  '[Teams] Load Teams Success',
  props<{ pagination: TeamsPagination; totalItems: number }>() // Added totalItems
);

export const loadTeamsFailure = createAction('[Teams] Load Teams Failure', props<{ error: any }>());

// Load Single Team
export const loadTeamById = createAction('[Teams] Load Team By Id', props<{ id: number }>());

export const loadTeamByIdSuccess = createAction('[Teams] Load Team By Id Success', props<{ team: Team }>());

export const loadTeamByIdFailure = createAction('[Teams] Load Team By Id Failure', props<{ error: any }>());

// Create Team
export const createTeam = createAction('[Teams] Create Team', props<{ team: Partial<Team>; teamTypeId?: number; parentId?: number | null }>());

export const createTeamSuccess = createAction('[Teams] Create Team Success', props<{ team: Team }>());

export const createTeamFailure = createAction('[Teams] Create Team Failure', props<{ error: any }>());

// Update Team
export const updateTeam = createAction('[Teams] Update Team', props<{ teamId: number; updates: Partial<Team> }>());

export const updateTeamSuccess = createAction('[Teams] Update Team Success', props<{ team: Team }>());

export const updateTeamFailure = createAction('[Teams] Update Team Failure', props<{ error: any }>());

// Reset Current Team
export const resetCurrentTeam = createAction('[Teams] Reset Current Team');

// Update Filter
export const updateFilter = createAction('[Teams] Update Filter', props<{ filter: string | null }>());

// Add these new actions for team membership
export const loadTeamMembers = createAction('[Team] Load Team Members', props<{ teamId: number }>());

export const loadTeamMembersSuccess = createAction('[Team] Load Team Members Success', props<{ teamId: number; members: TeamMember[] }>());

export const loadTeamMembersFailure = createAction('[Team] Load Team Members Failure', props<{ error: any }>());

export const addTeamMember = createAction('[Team] Add Team Member', props<{ teamId: number; userId: string; roles: string[] }>());

export const addTeamMemberSuccess = createAction('[Team] Add Team Member Success', props<{ teamId: number; member: TeamMember }>());

export const addTeamMemberFailure = createAction('[Team] Add Team Member Failure', props<{ error: any }>());

export const removeTeamMember = createAction('[Teams] Remove Team Member', props<{ teamId: number; userId: number; membershipId: number }>());

export const removeTeamMemberSuccess = createAction('[Teams] Remove Team Member Success', props<{ teamId: number; userId: number; membershipId: number }>());

export const removeTeamMemberFailure = createAction('[Teams] Remove Team Member Failure', props<{ error: any }>());

// Final Reviewer actions
export const loadFinalReviewers = createAction('[Teams] Load Final Reviewers', props<{ teamId: number }>());

export const loadFinalReviewersSuccess = createAction('[Teams] Load Final Reviewers Success', props<{ teamId: number; finalReviewers: TeamMember[] }>());

export const loadFinalReviewersFailure = createAction('[Teams] Load Final Reviewers Failure', props<{ error: any }>());

export const addFinalReviewer = createAction('[Teams] Add Final Reviewer', props<{ teamId: number; userId: string }>());

export const addFinalReviewerSuccess = createAction('[Teams] Add Final Reviewer Success', props<{ teamId: number; finalReviewer: TeamMember }>());

export const addFinalReviewerFailure = createAction('[Teams] Add Final Reviewer Failure', props<{ error: any }>());

export const removeFinalReviewer = createAction('[Teams] Remove Final Reviewer', props<{ teamId: number; userId: string }>());

export const removeFinalReviewerSuccess = createAction('[Teams] Remove Final Reviewer Success', props<{ teamId: number; userId: string }>());

export const removeFinalReviewerFailure = createAction('[Teams] Remove Final Reviewer Failure', props<{ error: any }>());

// Finance Reviewer actions
export const loadFinanceReviewers = createAction('[Teams] Load Finance Reviewers', props<{ teamId: number }>());

export const loadFinanceReviewersSuccess = createAction('[Teams] Load Finance Reviewers Success', props<{ teamId: number; financeReviewers: TeamMember[] }>());

export const loadFinanceReviewersFailure = createAction('[Teams] Load Finance Reviewers Failure', props<{ error: any }>());

export const addFinanceReviewer = createAction('[Teams] Add Finance Reviewer', props<{ teamId: number; userId: string }>());

export const addFinanceReviewerSuccess = createAction('[Teams] Add Finance Reviewer Success', props<{ teamId: number; financeReviewer: TeamMember }>());

export const addFinanceReviewerFailure = createAction('[Teams] Add Finance Reviewer Failure', props<{ error: any }>());

export const removeFinanceReviewer = createAction('[Teams] Remove Finance Reviewer', props<{ teamId: number; userId: string }>());

export const removeFinanceReviewerSuccess = createAction('[Teams] Remove Finance Reviewer Success', props<{ teamId: number; userId: string }>());

export const removeFinanceReviewerFailure = createAction('[Teams] Remove Finance Reviewer Failure', props<{ error: any }>());

// Gemba Lead actions
export const loadGembaLeads = createAction('[Teams] Load Gemba Leads', props<{ teamId: number }>());

export const loadGembaLeadsSuccess = createAction('[Teams] Load Gemba Leads Success', props<{ teamId: number; gembaLeads: TeamMember[] }>());

export const loadGembaLeadsFailure = createAction('[Teams] Load Gemba Leads Failure', props<{ error: any }>());

export const addGembaLead = createAction('[Teams] Add Gemba Lead', props<{ teamId: number; userId: string }>());

export const addGembaLeadSuccess = createAction('[Teams] Add Gemba Lead Success', props<{ teamId: number; gembaLead: TeamMember }>());

export const addGembaLeadFailure = createAction('[Teams] Add Gemba Lead Failure', props<{ error: any }>());

export const removeGembaLead = createAction('[Teams] Remove Gemba Lead', props<{ teamId: number; userId: string }>());

export const removeGembaLeadSuccess = createAction('[Teams] Remove Gemba Lead Success', props<{ teamId: number; userId: string }>());

export const removeGembaLeadFailure = createAction('[Teams] Remove Gemba Lead Failure', props<{ error: any }>());

// Update Team Finance Settings (combined action)
export const updateTeamFinanceSettings = createAction(
  '[Teams] Update Team Finance Settings',
  props<{
    teamId: number;
    settings: {
      requiresFinancialReview: boolean;
      financeThresholdId: number | null;
      financeReviewerUserIds: number[];
    };
  }>()
);

export const updateTeamFinanceSettingsSuccess = createAction('[Teams] Update Team Finance Settings Success', props<{ team: Team }>());

export const updateTeamFinanceSettingsFailure = createAction('[Teams] Update Team Finance Settings Failure', props<{ error: any }>());

// Update Team Reviewer Settings (combined action)
export const updateTeamReviewerSettings = createAction(
  '[Teams] Update Team Reviewer Settings',
  props<{
    teamId: number;
    settings: {
      requiresFinalReview: boolean;
      finalReviewerUserIds: number[];
    };
  }>()
);

export const updateTeamReviewerSettingsSuccess = createAction('[Teams] Update Team Reviewer Settings Success', props<{ team: Team }>());

export const updateTeamReviewerSettingsFailure = createAction('[Teams] Update Team Reviewer Settings Failure', props<{ error: any }>());

// Workspace Lead actions (read-only; mutations ride the combined settings PATCH)
export const loadWorkspaceLeads = createAction('[Teams] Load Workspace Leads', props<{ teamId: number }>());

export const loadWorkspaceLeadsSuccess = createAction('[Teams] Load Workspace Leads Success', props<{ teamId: number; workspaceLeads: TeamMember[] }>());

export const loadWorkspaceLeadsFailure = createAction('[Teams] Load Workspace Leads Failure', props<{ error: any }>());

// Update Team Workspace Management Settings (combined action)
export const updateTeamWorkspaceManagementSettings = createAction(
  '[Teams] Update Team Workspace Management Settings',
  props<{ teamId: number; settings: TeamWorkspaceManagementSettings }>()
);

export const updateTeamWorkspaceManagementSettingsSuccess = createAction('[Teams] Update Team Workspace Management Settings Success', props<{ team: Team }>());

export const updateTeamWorkspaceManagementSettingsFailure = createAction('[Teams] Update Team Workspace Management Settings Failure', props<{ error: any }>());
